import React, { useMemo, useId, useState, useRef, useCallback, useEffect } from 'react';

function buildSparklineGeometry(values, width, height, padding = 2) {
  if (!values?.length) return null;

  const numeric = values.map((v) => (v == null ? null : Number(v)));
  const defined = numeric.filter((v) => Number.isFinite(v));
  if (defined.length === 0) return null;

  const min = Math.min(...defined);
  const max = Math.max(...defined);
  const range = max - min || 1;
  const innerW = width - padding * 2;
  const innerH = height - padding * 2;
  const count = Math.max(2, numeric.length);
  const stepX = innerW / (count - 1);
  const baselineY = padding + innerH;

  const points = [];
  const source = numeric.length >= 2 ? numeric : [defined[0], defined[0]];
  source.forEach((value, index) => {
    if (!Number.isFinite(value)) return;
    points.push({
      x: padding + index * stepX,
      y: padding + innerH - ((value - min) / range) * innerH,
      value,
      index,
    });
  });

  if (points.length < 2) return null;

  const linePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
    .join(' ');

  const first = points[0];
  const last = points[points.length - 1];
  const areaPath = `${linePath} L ${last.x.toFixed(2)} ${baselineY.toFixed(2)} L ${first.x.toFixed(2)} ${baselineY.toFixed(2)} Z`;

  return { linePath, areaPath, min, max, points };
}

function formatTemp(value) {
  if (value == null || !Number.isFinite(value)) return '--';
  const abs = Math.abs(value);
  if (abs >= 10) return value.toFixed(1);
  if (abs >= 1) return value.toFixed(2);
  return value.toFixed(3);
}

export default function TemperatureSparkline({
  values = [],
  width = 200,
  height = 44,
  color = '#24a148',
  loading = false,
  isDark = false,
  label = 'Last 6h',
  onPointClick,
}) {
  const gradientId = useId().replace(/:/g, '');
  const svgRef = useRef(null);
  const [hoverPoint, setHoverPoint] = useState(null);
  const [selectedPoint, setSelectedPoint] = useState(null);

  const geometry = useMemo(
    () => buildSparklineGeometry(values, width, height),
    [values, width, height]
  );

  const gridColor = isDark ? '#393939' : '#e0e0e0';
  const textColor = isDark ? '#a8a8a8' : '#6f6f6f';
  const tooltipBg = isDark ? '#262626' : '#ffffff';
  const tooltipBorder = isDark ? '#525252' : '#c6c6c6';

  const handleMouseMove = useCallback(
    (e) => {
      if (!geometry?.points || !svgRef.current) return;
      const rect = svgRef.current.getBoundingClientRect();
      const mouseX = ((e.clientX - rect.left) / rect.width) * width;
      let nearest = null;
      let minDist = Infinity;
      for (const pt of geometry.points) {
        const d = Math.abs(pt.x - mouseX);
        if (d < minDist) {
          minDist = d;
          nearest = pt;
        }
      }
      setHoverPoint(nearest);
    },
    [geometry, width]
  );

  const handleMouseLeave = useCallback(() => setHoverPoint(null), []);

  const findNearestPoint = useCallback(
    (clientX) => {
      if (!geometry?.points || !svgRef.current) return null;
      const rect = svgRef.current.getBoundingClientRect();
      const pointerX = ((clientX - rect.left) / rect.width) * width;
      return geometry.points.reduce(
        (nearest, point) => (
          !nearest || Math.abs(point.x - pointerX) < Math.abs(nearest.x - pointerX)
            ? point
            : nearest
        ),
        null,
      );
    },
    [geometry, width],
  );

  const handleClick = useCallback(
    (event) => {
      if (!onPointClick) return;
      const point = findNearestPoint(event.clientX);
      if (!point) return;
      setSelectedPoint(point);
      onPointClick({ index: point.index, value: point.value });
    },
    [findNearestPoint, onPointClick],
  );

  useEffect(() => {
    setSelectedPoint(null);
  }, [values]);

  if (loading) {
    return (
      <div style={{ marginTop: 8 }}>
        <div style={{ fontSize: '0.65rem', color: textColor, marginBottom: 4 }}>{label}</div>
        <div
          style={{
            width,
            height,
            borderRadius: 4,
            background: isDark ? '#262626' : '#f4f4f4',
          }}
        />
      </div>
    );
  }

  if (!geometry) return null;

  const { linePath, areaPath, min, max, points } = geometry;

  const activePoint = hoverPoint || selectedPoint;
  const tooltipText = activePoint ? `${formatTemp(activePoint.value)}°` : null;
  const tooltipX = activePoint ? Math.min(Math.max(activePoint.x, 30), width - 30) : 0;
  const tooltipY = activePoint ? Math.max(activePoint.y - 10, 8) : 0;

  return (
    <div style={{ marginTop: 8, position: 'relative' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '0.65rem',
          color: textColor,
          marginBottom: 4,
        }}
      >
        <span>{label}</span>
        <span>
          {activePoint ? (
            <strong style={{ color }}>{formatTemp(activePoint.value)}°</strong>
          ) : (
            `${formatTemp(min)}° – ${formatTemp(max)}°`
          )}
        </span>
      </div>
      <svg
        ref={svgRef}
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label="Temperature trend"
        style={{ display: 'block', cursor: onPointClick ? 'pointer' : 'crosshair' }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={isDark ? 0.35 : 0.28} />
            <stop offset="100%" stopColor={color} stopOpacity={0.04} />
          </linearGradient>
        </defs>
        <line
          x1={0}
          y1={height - 1}
          x2={width}
          y2={height - 1}
          stroke={gridColor}
          strokeWidth={1}
        />
        <path d={areaPath} fill={`url(#${gradientId})`} stroke="none" />
        <path
          d={linePath}
          fill="none"
          stroke={color}
          strokeWidth={1.75}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {activePoint && (
          <g pointerEvents="none">
            <line
              x1={activePoint.x}
              y1={0}
              x2={activePoint.x}
              y2={height}
              stroke={color}
              strokeWidth={1}
              strokeDasharray="3 3"
              opacity={0.5}
            />
            <circle
              cx={activePoint.x}
              cy={activePoint.y}
              r={selectedPoint?.index === activePoint.index ? 4 : 3}
              fill={color}
              stroke={isDark ? '#161616' : '#ffffff'}
              strokeWidth={1.5}
            />
            {tooltipText && (
              <g>
                <rect
                  x={tooltipX - 22}
                  y={tooltipY - 14}
                  width={44}
                  height={18}
                  rx={4}
                  fill={tooltipBg}
                  stroke={tooltipBorder}
                  strokeWidth={1}
                />
                <text
                  x={tooltipX}
                  y={tooltipY - 1}
                  textAnchor="middle"
                  fontSize={11}
                  fontWeight={600}
                  fill={textColor}
                >
                  {tooltipText}
                </text>
              </g>
            )}
          </g>
        )}
      </svg>
    </div>
  );
}
